// Pure-function audio-route selection policy.
//
// No network input drives audio routing — every parameter to every function
// in this file is locally typed and locally derived. The policy matches
// browser/mobile defaults (earpiece for audio calls, speakerphone for video
// calls), with two safety overrides: CarPlay always wins (driver safety) and
// we refuse to hijack AirPods / Bluetooth / wired / AirPlay when the user is
// mid-listening to something else. A user-initiated picker tap locks the
// choice for the rest of the call so a transient disconnect cannot thrash the
// route under the user's finger.
//
// Mirrors iOS AudioRoutePolicy.swift exactly. Priority ladder and reason
// strings are kept identical for cross-platform traceability.

import type {
  AudioRouteDescriptor,
  AudioSessionSnapshot,
} from './AudioRoute';

/** Whether the call carries video (→ speakerphone default) or audio only (→ earpiece default). */
export type CallMedia = 'audio' | 'video';

/**
 * The result of a route-selection decision.
 *
 * `lockedByUser`: true once the user has explicitly tapped the picker, or
 * when the user's previous choice was restored. When true, the audio-session
 * adapter MUST ignore subsequent auto-reroute attempts for the rest of the
 * call. The lock is per-call — it resets when a new call starts.
 */
export interface AudioRouteSelection {
  readonly chosen: AudioRouteDescriptor;
  /** Human-readable explanation for the decision (matches iOS reason strings). */
  readonly reason: string;
  /**
   * True once the user has explicitly tapped the picker. The adapter must
   * consult this flag and refuse to auto-reroute the call when a Bluetooth
   * port briefly disconnects/reconnects.
   */
  readonly lockedByUser: boolean;
}

/**
 * Picks the initial route at call start. Priority (highest first):
 *
 *   1. CarPlay       — driver safety, overrides every preference.
 *   2. User's previous choice — if still available (by portIdentifier, or by
 *                     kind for system-virtual routes like earpiece/speakerphone).
 *   3. AirPods       — but only if !snapshot.otherAudioPlaying.
 *   4. Bluetooth     — same otherAudioPlaying gate.
 *   5. Wired         — same gate.
 *   6. AirPlay       — same gate.
 *   7. Speakerphone  — video calls only.
 *   8. Earpiece      — audio call default.
 *   9. Fallback      — keep snapshot.currentRoute if availableRoutes is empty.
 *
 * @param snapshot     Local audio system state. Built by the consumer from
 *                     enumerateDevices(); never touches MediaDevices itself.
 * @param settings     Previously-remembered user choice for this peer, if any.
 * @param callMedia    Whether the call is audio-only or video.
 */
export function selectInitialRoute(
  snapshot: AudioSessionSnapshot,
  settings: { userPreviousChoice?: AudioRouteDescriptor } | undefined,
  callMedia: CallMedia,
): AudioRouteSelection {
  const available = snapshot.availableRoutes;

  // 1. CarPlay always wins — driver safety overrides every preference.
  const carplay = available.find((r) => r.kind === 'car');
  if (carplay) {
    return {
      chosen: carplay,
      reason: 'carplay safety override',
      lockedByUser: false,
    };
  }

  // 2. User's previous choice for this peer, if still available.
  const prev = settings?.userPreviousChoice;
  if (prev) {
    const match = available.find((r) => sameRoute(r, prev));
    if (match) {
      return {
        chosen: match,
        reason: 'user previous choice',
        lockedByUser: true,
      };
    }
  }

  // 3–6. External audio routes, gated by !otherAudioPlaying.
  if (!snapshot.otherAudioPlaying) {
    const airpods = available.find((r) => r.kind === 'airpods');
    if (airpods) {
      return {
        chosen: airpods,
        reason: 'airpods available, not interrupting other audio',
        lockedByUser: false,
      };
    }

    const bluetooth = available.find((r) => r.kind === 'bluetooth');
    if (bluetooth) {
      return {
        chosen: bluetooth,
        reason: 'bluetooth headphones available',
        lockedByUser: false,
      };
    }

    const wired = available.find((r) => r.kind === 'wired');
    if (wired) {
      return {
        chosen: wired,
        reason: 'wired headphones available',
        lockedByUser: false,
      };
    }

    const airplay = available.find((r) => r.kind === 'airplay');
    if (airplay) {
      return {
        chosen: airplay,
        reason: 'airplay receiver available',
        lockedByUser: false,
      };
    }
  }

  // 7. Video call → speakerphone.
  if (callMedia === 'video') {
    const speaker = available.find((r) => r.kind === 'speakerphone');
    if (speaker) {
      return {
        chosen: speaker,
        reason: 'video call default to speakerphone',
        lockedByUser: false,
      };
    }
  }

  // 8. Audio call → earpiece.
  const earpiece = available.find((r) => r.kind === 'earpiece');
  if (earpiece) {
    return {
      chosen: earpiece,
      reason: 'audio call default',
      lockedByUser: false,
    };
  }

  // 9. Degenerate fallback: nothing available — keep the current route.
  return {
    chosen: snapshot.currentRoute,
    reason: 'fallback to current',
    lockedByUser: false,
  };
}

/**
 * User has explicitly tapped the picker. Returns a locked selection for the
 * route chosen. The caller must store this for the rest of the call and
 * ignore subsequent auto-reroute events while `lockedByUser` is true.
 *
 * The lock is per-call. When the call ends, discard the selection and call
 * `selectInitialRoute` fresh on the next call.
 *
 * @param route                  The route the user tapped.
 * @param currentLockedSelection The selection currently in effect (unused in
 *                               the policy itself; kept for API parity with
 *                               iOS `userPickedRoute(_:currentLockedSelection:)`).
 */
export function applyUserPick(
  route: AudioRouteDescriptor,
  _currentLockedSelection?: AudioRouteSelection,
): AudioRouteSelection {
  return {
    chosen: route,
    reason: 'user picker tap',
    lockedByUser: true,
  };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Two routes are "the same" for selection purposes if their port identifiers
 * match, falling back to kind when the port is undefined (system-virtual
 * routes like earpiece/speakerphone have no stable browser device ID).
 *
 * Mirrors iOS `AudioRoutePolicy.sameRoute(_:_:)` exactly.
 */
function sameRoute(a: AudioRouteDescriptor, b: AudioRouteDescriptor): boolean {
  if (a.portIdentifier !== undefined && b.portIdentifier !== undefined) {
    return a.portIdentifier === b.portIdentifier;
  }
  return a.kind === b.kind;
}
